// src/components/ServiceTemplate/ServiceTemplateBase.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { useToast } from "@/hooks/use-toast";

// =========================================================================
// TYPES
// =========================================================================
export interface LoggedUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  image?: string;
  avatar?: string;
  company?: string;
  company_logo?: string;
  license_number?: string;
}

export interface ServiceData {
  id?: number | string;
  title?: string;
  headline?: string;
  serviceType?: string;
  service_type?: string;
  description?: string;

  // Images
  heroImage?: string;
  hero_image?: string;
  hero_image_url?: string;
  image?: string;
  secondaryImage?: string;

  // ✅ Photos array from backend
  photos?: string[];
  photos_urls?: string[];

  // Contact info
  phone?: string;
  email?: string;
  website?: string;
  address?: string;

  // Features
  features?: (string | FeatureItem)[];

  // Subject for email
  subject?: string;

  // Allow additional fields
  [key: string]: any;
}

export interface FeatureItem {
  id?: number;
  name?: string;
  feature_name?: string;
}

export interface ServiceFlyerData {
  title: string;
  headline: string;
  serviceType: string;
  description: string;
  heroImage: string;
  secondaryImage: string;
  photos: string[];
  phone: string;
  email: string;
  website: string;
  address: string;
  features: string[];
  subject: string;
  // Agent data (from logged user)
  agentName: string;
  agentPhone: string;
  agentEmail: string;
  agentAvatar: string;
  company: string;
  companyLogo: string;
  license_number?: string;
}

export function sanitizeText(value?: string | null): string {
  return String(value ?? "")
    .replace(/\r?\n|\r/g, " ")
    .trim();
}

// =========================================================================
// NORMALIZE FEATURES - Convert any format to string array
// =========================================================================
export function normalizeFeatures(features: any): string[] {
  if (!features) return [];

  if (Array.isArray(features)) {
    return features
      .map((f) => {
        if (typeof f === "string") return f.trim();
        if (typeof f === "object" && f !== null) {
          return (f.name || f.feature_name || "").trim();
        }
        return "";
      })
      .filter((f) => f.length > 0);
  }

  return [];
}

// =========================================================================
// GET IMAGE URL - Check multiple field names for HERO IMAGE
// =========================================================================
export function getServiceImageUrl(data: ServiceData | undefined): string {
  if (!data) return "";

  return (
    data.heroImage ||
    data.hero_image_url ||
    data.hero_image ||
    data.image ||
    ""
  );
}

// =========================================================================
// ✅ NEW: GET SECONDARY IMAGE URL - Check photos array
// =========================================================================
export function getSecondaryImageUrl(data: ServiceData | undefined): string {
  if (!data) return "";

  const heroImage = getServiceImageUrl(data);

  // First check explicit secondaryImage field
  if (data.secondaryImage && data.secondaryImage !== heroImage) {
    return data.secondaryImage;
  }

  // Then check photos_urls array (absolute URLs from backend)
  if (data.photos_urls && Array.isArray(data.photos_urls) && data.photos_urls.length > 0) {
    const secondaryPhoto = data.photos_urls.find((url) => url && url !== heroImage);
    if (secondaryPhoto) return secondaryPhoto;
  }

  // Then check photos array (relative URLs)
  if (data.photos && Array.isArray(data.photos) && data.photos.length > 0) {
    const photoUrl = data.photos.find((url) => url && url !== heroImage);
    if (!photoUrl) return "";
    // If it's already an absolute URL, return as-is
    if (photoUrl.startsWith('http')) {
      return photoUrl;
    }
    // Otherwise, it might need the base URL prepended
    return photoUrl;
  }

  return "";
}

// =========================================================================
// HOOK PARAMS
// =========================================================================
interface UseServiceTemplateParams {
  serviceData?: ServiceData | null;
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updated: Partial<ServiceFlyerData>) => void;
}

// =========================================================================
// MAIN HOOK
// =========================================================================
// =========================================================================
// MAIN HOOK - FIXED VERSION
// =========================================================================
export function useServiceTemplateBase({
  serviceData,
  onDirtyChange,
  onChange,
}: UseServiceTemplateParams) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const secondaryFileInputRef = useRef<HTMLInputElement | null>(null);

  // ✅ FIX 1: Use useMemo for loggedUser to prevent recreation on every render
  const loggedUser: LoggedUser = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  // =========================================================================
  // BUILD INITIAL DATA - Remove console.log from here
  // =========================================================================
  const buildServiceFlyerData = useCallback(
    (data: ServiceData | null | undefined): ServiceFlyerData => {
      const features = normalizeFeatures(data?.features);
      const heroImage = getServiceImageUrl(data);
      const secondaryImage = getSecondaryImageUrl(data);

      let photos: string[] = [];
      if (data?.photos_urls && Array.isArray(data.photos_urls)) {
        photos = data.photos_urls;
      } else if (data?.photos && Array.isArray(data.photos)) {
        photos = data.photos;
      }

      // ✅ FIX 2: Remove console.log from here - it was causing multiple logs

      return {
        title: data?.title || data?.headline || "",
        headline: data?.headline || data?.title || "",
        serviceType: data?.serviceType || data?.service_type || "",
        description: data?.description || "",
        heroImage: heroImage,
        secondaryImage: secondaryImage,
        photos: photos,
        phone: data?.phone || "",
        email: data?.email || "",
        website: data?.website || "",
        address: data?.address || "",
        features: features,
        subject: data?.subject || "",
        agentName: loggedUser?.first_name
          ? `${loggedUser.first_name} ${loggedUser.last_name ?? ""}`.trim()
          : "",
        agentPhone: (loggedUser as any)?.phone_number ?? "",
        agentEmail: loggedUser?.email ?? "",
        agentAvatar: (loggedUser as any)?.avatar ?? "",
        company: (loggedUser as any)?.company ?? "",
        companyLogo: (loggedUser as any)?.company_logo ?? "",
        license_number: (loggedUser as any)?.license_number ?? "",
      };
    },
    [loggedUser]
  );

  // =========================================================================
  // STATE
  // =========================================================================
  const [flyerData, setFlyerData] = useState<ServiceFlyerData>(() =>
    buildServiceFlyerData(serviceData)
  );
  const [userData, setUserData] = useState<LoggedUser>({});
  const [loading, setLoading] = useState(true);

  // ✅ FIX 3: Track initialization and ID changes properly
  const prevServiceIdRef = useRef<number | string | undefined>();
  const isInitializedRef = useRef(false);

  // =========================================================================
  // ✅ FIX 4: SYNC FROM PROPS - Only run when ID actually changes
  // =========================================================================
  useEffect(() => {
    if (!serviceData) return;

    const currentId = serviceData.id;
    const idChanged = prevServiceIdRef.current !== currentId;

    // Only sync on first load or when ID changes
    if (!isInitializedRef.current || idChanged) {
      prevServiceIdRef.current = currentId;
      isInitializedRef.current = true;

      // Only log once per ID change
      console.log("[useServiceTemplateBase] Syncing data for ID:", currentId);

      const newData = buildServiceFlyerData(serviceData);

      setFlyerData((prev) => ({
        ...prev,
        ...newData,
        // Preserve agent data if already fetched
        agentName: prev.agentName || newData.agentName,
        agentPhone: prev.agentPhone || newData.agentPhone,
        agentEmail: prev.agentEmail || newData.agentEmail,
        agentAvatar: prev.agentAvatar || newData.agentAvatar,
        company: prev.company || newData.company,
        companyLogo: prev.companyLogo || newData.companyLogo,

      }));
    }
  }, [serviceData?.id]); // ✅ Only depend on ID, not entire serviceData object

  // =========================================================================
  // FETCH USER DATA - Only once on mount
  // =========================================================================
  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(endpoints.me);
        const data = response.data;

        if (!isMounted) return;

        setUserData(data);

        setFlyerData((prev) => ({
          ...prev,
          agentName: `${data.first_name ?? ""} ${data.last_name ?? ""}`.trim() || prev.agentName,
          agentPhone: data.phone_number ?? prev.agentPhone,
          agentEmail: data.email ?? prev.agentEmail,
          agentAvatar: data.avatar ?? prev.agentAvatar,
          company: data.company ?? prev.company,
          companyLogo: data.company_logo ?? prev.companyLogo,
          license_number: data.license_number ?? prev.license_number,
        }));
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency - run once

  // =========================================================================
  // INPUT CHANGE HANDLER
  // =========================================================================
  const handleInputChange = useCallback(
    (field: keyof ServiceFlyerData, value: any) => {
      setFlyerData((prev) => {
        const next = { ...prev, [field]: value };
        onDirtyChange?.(true);
        onChange?.({ [field]: value });
        return next;
      });
    },
    [onDirtyChange, onChange]
  );

  const saveFeaturesToBackend = useCallback(
    async (featuresToSave: string[]) => {
      if (!serviceData?.id) return;

      try {
        const formData = new FormData();
        formData.append(
          "features",
          JSON.stringify(
            featuresToSave
              .map((feature) => String(feature ?? "").trim())
              .filter((feature) => feature.length > 0)
          )
        );

        await axiosInstance.patch(
          `${endpoints.service_listings}${serviceData.id}/`,
          formData
        );
      } catch (error) {
        console.error("Failed to save service features:", error);
        toast({
          title: "Warning",
          description: "Unable to save feature changes right now.",
          variant: "destructive",
          duration: 1200,
        });
      }
    },
    [serviceData?.id, toast]
  );

  const saveFieldToBackend = useCallback(
    async (field: string, value: any) => {
      if (!serviceData?.id) return;

      try {
        const formData = new FormData();

        if (field === "features" && Array.isArray(value)) {
          formData.append(
            "features",
            JSON.stringify(
              value
                .map((feature: any) => String(feature ?? "").trim())
                .filter((feature: string) => feature.length > 0)
            )
          );
        } else {
          formData.append(field, String(value ?? ""));
        }

        await axiosInstance.patch(
          `${endpoints.service_listings}${serviceData.id}/`,
          formData
        );
      } catch (error) {
        console.error(`Failed to save ${field}:`, error);
        toast({
          title: "Warning",
          description: `Unable to save ${field} changes right now.`,
          variant: "destructive",
          duration: 1200,
        });
      }
    },
    [serviceData?.id, toast]
  );

  // =========================================================================
  // FILE CHANGE HANDLER (Hero Image)
  // =========================================================================
  const handleFileChange = useCallback(
    async (
      e: React.ChangeEvent<HTMLInputElement>,
      options?: { captureOriginal?: boolean; onOriginalCaptured?: (url: string) => void }
    ) => {
      // captureOriginal defaults to true: a genuinely new photo pick becomes
      // its own untouched "original" so Edit Image can always re-crop from
      // the full source later. The Edit Image crop-confirm path reuses this
      // same handler via a synthetic file-input event and passes
      // captureOriginal: false, so re-cropping an already-cropped photo
      // doesn't overwrite the true original with the crop.
      const captureOriginal = options?.captureOriginal ?? true;
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid File",
          description: "Please upload an image file",
          variant: "destructive",
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Image must be less than 10 MB",
          variant: "destructive",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setFlyerData((prev) => ({ ...prev, heroImage: dataUrl }));
        onDirtyChange?.(true);
        onChange?.({ heroImage: dataUrl });
        if (captureOriginal) options?.onOriginalCaptured?.(dataUrl);
      };
      reader.readAsDataURL(file);

      if (serviceData?.id) {
        try {
          const formData = new FormData();
          formData.append("hero_image", file);

          const response = await axiosInstance.patch(
            `${endpoints.service_listings}${serviceData.id}/`,
            formData,
            {
              headers: { "Content-Type": undefined as any },
              transformRequest: [(data) => data],
            }
          );

          if (response.data?.hero_image_url) {
            setFlyerData((prev) => ({
              ...prev,
              heroImage: response.data.hero_image_url,
            }));
            onChange?.({ heroImage: response.data.hero_image_url });

            if (captureOriginal) {
              options?.onOriginalCaptured?.(response.data.hero_image_url);
              axiosInstance
                .patch(`${endpoints.service_listings}${serviceData.id}/`, {
                  original_hero_image: response.data.hero_image_url,
                })
                .catch((err) => {
                  console.error("Failed to seed original hero image:", err);
                });
            }
          }

          toast({
            title: "Success",
            description: "Image uploaded successfully",
          });
        } catch (error) {
          console.error("Failed to upload image:", error);
          toast({
            title: "Warning",
            description: "Image preview set but server upload failed",
            variant: "destructive",
            duration: 1000,
          });
        }
      }

      e.target.value = "";
    },
    [serviceData?.id, onDirtyChange, onChange, toast]
  );

  // =========================================================================
  // SECONDARY IMAGE HANDLER
  // =========================================================================
  const handleSecondaryImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid File",
          description: "Please upload an image file",
          variant: "destructive",
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Image must be less than 10 MB",
          variant: "destructive",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setFlyerData((prev) => ({
          ...prev,
          secondaryImage: dataUrl,
          photos: [dataUrl, ...(prev.photos || []).slice(1)],
        }));
        onDirtyChange?.(true);
        onChange?.({ secondaryImage: dataUrl });
      };
      reader.readAsDataURL(file);

      if (serviceData?.id) {
        try {
          const formData = new FormData();
          formData.append("photo_1", file);

          const response = await axiosInstance.patch(
            `${endpoints.service_listings}${serviceData.id}/`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );

          if (response.data?.photos_urls && response.data.photos_urls.length > 0) {
            setFlyerData((prev) => ({
              ...prev,
              secondaryImage: response.data.photos_urls[0],
              photos: response.data.photos_urls,
            }));
            onChange?.({ secondaryImage: response.data.photos_urls[0] });
          } else if (response.data?.photos && response.data.photos.length > 0) {
            setFlyerData((prev) => ({
              ...prev,
              secondaryImage: response.data.photos[0],
              photos: response.data.photos,
            }));
            onChange?.({ secondaryImage: response.data.photos[0] });
          }

          toast({
            title: "Success",
            description: "Secondary image uploaded successfully",
          });
        } catch (error) {
          console.error("Failed to upload secondary image:", error);
          toast({
            title: "Warning",
            description: "Image preview set but server upload failed",
            variant: "destructive",
            duration: 1000,
          });
        }
      }

      e.target.value = "";
    },
    [serviceData?.id, onDirtyChange, onChange, toast]
  );

  // =========================================================================
  // FEATURE HANDLERS
  // =========================================================================
  const addFeature = useCallback(() => {
    if (flyerData.features.length >= 10) {
      toast({
        title: "Feature Limit Reached",
        description: "You can add a maximum of 10 features.",
        variant: "destructive",
      });
      return;
    }

    setFlyerData((prev) => ({ ...prev, features: [...prev.features, ""] }));
    onDirtyChange?.(true);
  }, [flyerData.features.length, onDirtyChange, toast]);

  const handleFeatureChange = useCallback(
    (index: number, value: string) => {
      setFlyerData((prev) => {
        const features = [...prev.features];
        features[index] = value;
        return { ...prev, features };
      });

      if (value.trim()) {
        onDirtyChange?.(true);
      }
    },
    [onDirtyChange]
  );

  const handleFeatureBlur = useCallback(
    (index: number) => {
      const currentFeatures = flyerData.features;
      const feature = currentFeatures[index];
      const nextFeatures = !feature || !String(feature).trim()
        ? currentFeatures.filter((_, i) => i !== index)
        : currentFeatures;

      setFlyerData((prev) => ({ ...prev, features: nextFeatures }));
      onDirtyChange?.(true);
      onChange?.({ features: nextFeatures });
      void saveFeaturesToBackend(nextFeatures);
    },
    [flyerData.features, onChange, onDirtyChange, saveFeaturesToBackend]
  );

  const handleFeatureDelete = useCallback(
    (index: number) => {
      const nextFeatures = flyerData.features.filter((_, i) => i !== index);
      setFlyerData((prev) => ({ ...prev, features: nextFeatures }));
      onDirtyChange?.(true);
      onChange?.({ features: nextFeatures });
      void saveFeaturesToBackend(nextFeatures);
    },
    [flyerData.features, onDirtyChange, onChange, saveFeaturesToBackend]
  );

  // =========================================================================
  // REMOVE IMAGE HANDLERS
  // =========================================================================
  const removeHeroImage = useCallback(() => {
    setFlyerData((prev) => ({ ...prev, heroImage: "" }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: "" });
    toast({
      title: "Image Removed",
      description: "Hero image has been removed",
    });
  }, [onDirtyChange, onChange, toast]);

  const removeSecondaryImage = useCallback(() => {
    setFlyerData((prev) => ({
      ...prev,
      secondaryImage: "",
      photos: prev.photos.slice(1),
    }));
    onDirtyChange?.(true);
    onChange?.({ secondaryImage: "" });
  }, [onDirtyChange, onChange]);

  // =========================================================================
  // FORCE REFRESH
  // =========================================================================
  const forceRefresh = useCallback(() => {
    if (serviceData) {
      setFlyerData(buildServiceFlyerData(serviceData));
    }
  }, [serviceData, buildServiceFlyerData]);

  // =========================================================================
  // RETURN
  // =========================================================================
  return {
    flyerData,
    setFlyerData,
    userData,
    loading,
    fileInputRef,
    secondaryFileInputRef,
    handleInputChange,
    handleFileChange,
    handleSecondaryImageChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    removeHeroImage,
    removeSecondaryImage,
    forceRefresh,
    saveFieldToBackend,
    toast,
  };
}

export default useServiceTemplateBase;
